import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { NewsService } from "../../services/news_service";
import config from "../../config/config";
import styles from "./News.module.css";
import Subscription from "../Subscription/Subscription";
import {
  FaFire,
  FaRegNewspaper,
  FaArrowRight,
  FaCircle,
} from "react-icons/fa";
import { useNewsState } from "../../states/useNewsState";
import news from "./news.jpg"
const News = () => {
  const newsService = NewsService();

  // Use the imported state
  const {
    newsData,
    setNewsData,
    filteredNews,
    setFilteredNews,
    loading,
    setLoading,
    activeCategory,
    setActiveCategory,
    selectedNews,
    setSelectedNews,
    isModalOpen,
    setIsModalOpen
  } = useNewsState();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchNewsData = async () => {
      try {
        await newsService.fetchAllNews(setNewsData);
      } catch (error) {
        console.error("Error fetching news data", error);
        setNewsData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchNewsData();
  }, []);

  useEffect(() => {
    if (Array.isArray(newsData) && newsData.length > 0) {
      if (activeCategory === "all") {
        setFilteredNews(newsData);
      } else {
        const filtered = newsData.filter(
          (news) =>
            news &&
            news.category?.toLowerCase() === activeCategory.toLowerCase()
        );
        setFilteredNews(filtered);
      }
    } else {
      setFilteredNews([]);
    }
  }, [newsData, activeCategory]);

  // Handle read more click
  const handleReadMore = (news) => {
    setSelectedNews(news);
    setIsModalOpen(true);
    // Prevent body scroll when modal is open
    document.body.style.overflow = "hidden";
  };

  // Handle modal close
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedNews(null);
    // Restore body scroll
    document.body.style.overflow = "unset";
  };

  // Close modal when clicking outside
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleCloseModal();
    }
  };

  // Safe data access functions
  const getFullImageUrl = (imagePath) => {
    if (!imagePath) return "/default-news.jpg";
    if (imagePath.startsWith("http")) return imagePath;
    return `${config.baseURL}${imagePath}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "No date";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch (error) {
      return "Invalid date";
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return "";
    try {
      return new Date(dateString).toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      return "";
    }
  };

  const featuredNews = Array.isArray(newsData)
    ? newsData.filter(news => news.is_featured === true)
    : [];

  const categories = [
    { key: "all", label: "All News" },
    { key: "match", label: "Match Reports" },
    { key: "transfer", label: "Transfers" },
    { key: "interview", label: "Interviews" },
    { key: "analysis", label: "Analysis" },
    { key: "announcement", label: "Announcements" },
  ];

  const getCategoryColor = (category) => {
    const colors = {
      match: "#e74c3c",
      transfer: "#3498db",
      interview: "#9b59b6",
      analysis: "#f39c12",
      announcement: "#2ecc71",
    };
    return colors[category?.toLowerCase()] || "#e74c3c";
  };

  // Safe content extraction
  const getContentExcerpt = (content, length = 120) => {
    if (!content) return "No content available...";
    const textContent = content.replace(/<[^>]*>/g, "");
    return textContent.length > length
      ? textContent.substring(0, length) + "..."
      : textContent;
  };

  // Get full content without HTML tags
  const getFullContent = (content) => {
    if (!content) return "No content available.";
    return content.replace(/<[^>]*>/g, "");
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <p>Loading Latest News...</p>
      </div>
    );
  }

  return (
    <div className={styles.newsPage}>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroBg}><img src={news} alt="News" /></div>
        <div className={styles.heroOverlay}></div>
        <div className={styles.heroContent}>
          <div className={styles.container}>
            <h1 className={`${styles.heroTitle} ${styles.heroTitleAnim}`}>
              <span>Latest News</span>
            </h1>

            <p className={`${styles.heroSubtitle} ${styles.heroSubtitleAnim}`}>
              Stay updated with the latest from Royal Premier League - match
              reports, transfers, interviews and more
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className={styles.filterSection}>
        <div className={styles.container}>
          <div className={styles.filterTabs}>
            {categories.map((category) => (
              <button
                key={category.key}
                className={`${styles.filterTab} ${activeCategory === category.key ? styles.active : ""
                  }`}
                onClick={() => setActiveCategory(category.key)}
                style={{
                  "--category-color": getCategoryColor(category.key),
                }}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* News Grid Section */}
      <section className={styles.newsGridSection}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>
              {activeCategory === "all"
                ? "All News"
                : `${categories.find((c) => c.key === activeCategory)?.label}`}
            </h2>
            <div className={styles.newsCount}>
              {filteredNews.length}{" "}
              {filteredNews.length === 1 ? "article" : "articles"}
            </div>
          </div>

          {filteredNews.length === 0 ? (
            <div className={styles.noNews}>
              <div className={styles.noNewsIcon}>  <FaRegNewspaper /></div>
              <h3>No News Found</h3>
              <p>
                There are no {activeCategory !== "all" ? activeCategory : ""}{" "}
                articles at the moment.
              </p>
            </div>
          ) : (
            <div className={styles.newsGrid}>
              {filteredNews.map((news, index) => (
                <article
                  key={news.id || index}
                  className={styles.newsCard}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className={styles.newsImage}>
                    <img
                      src={getFullImageUrl(news.image)}
                      alt={news.title}
                      className={styles.image}
                      onError={(e) => {
                        e.target.src = "/default-news.jpg";
                      }}
                    />
                    <div
                      className={styles.newsCategory}
                      style={{
                        backgroundColor: getCategoryColor(news.category),
                      }}
                    >
                      {news.category}
                    </div>
                    {news.is_featured && (
                      <div className={styles.featuredBadge}>Featured</div>
                    )}
                    <div className={styles.newsOverlay}></div>
                  </div>

                  <div className={styles.newsContent}>
                    <div className={styles.newsMeta}>
                      <span className={styles.newsDate}>
                        {formatDate(news.published_at)}
                      </span>
                      {news.author && (
                        <span className={styles.newsAuthor}>
                          By {news.author}
                        </span>

                      )}
                    </div>

                    <h3 className={styles.newsTitle}>{news.title}</h3>
                    <p
                      className={styles.newsExcerpt}
                      dangerouslySetInnerHTML={{
                        __html: getContentExcerpt(news.content, 120),
                      }}
                    ></p>


                    <div className={styles.newsFooter}>
                      <Link to={`/news/${news.id}`} className={styles.readMore}>
                        Read More <FaArrowRight />
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
      {/* Trending News Sidebar */}
      {featuredNews.length > 0 && (
        <section className={styles.trendingSection}>
          <div className={styles.container}>
            <div className={styles.trendingHeader}>
              <h3 className={styles.trendingTitle}><FaFire />Trending Now</h3>
              <div className={styles.trendingIndicator}>
                <FaCircle className={styles.pulseDot} />
                Live Updates
              </div>
            </div>

            <div className={styles.trendingList}>
              {featuredNews.map((news, index) => (
                <div
                  key={news.id || index}
                  className={styles.trendingItem}
                  onClick={() => handleReadMore(news)}
                >
                  <div className={styles.trendingNumber}>{index + 1}</div>
                  <div className={styles.trendingContent}>
                    <h4 className={styles.trendingNewsTitle}>
                      {news.title}
                    </h4>
                    <div className={styles.trendingMeta}>
                      <span className={styles.trendingCategory}>
                        {news.category}
                      </span>
                      <span className={styles.trendingTime}>
                        {formatTime(news.published_at)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
      <Subscription />
    </div>
  );
};

export default News;